var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import { getComponentElements, getEventTarget } from "./utils.js";
import { ChildrenManager } from "./managers/ChildrenManager.js";
import { RefsManager } from "./managers/RefsManager.js";
import { ServicesManager } from "./managers/ServicesManager.js";
import { EventsManager } from "./managers/EventsManager.js";
import { OptionsManager } from "./managers/OptionsManager.js";
import { noop, isDev, isFunction, isArray } from "../utils/index.js";
let id = 0;
function createAndTestManagers(instance) {
  [
    {
      prop: "__options",
      constructorName: "OptionsManager",
      constructor: OptionsManager
    },
    {
      prop: "__services",
      constructorName: "ServicesManager",
      constructor: ServicesManager
    },
    {
      prop: "__events",
      constructorName: "EventsManager",
      constructor: EventsManager
    },
    {
      prop: "__refs",
      constructorName: "RefsManager",
      constructor: RefsManager
    },
    {
      prop: "__children",
      constructorName: "ChildrenManager",
      constructor: ChildrenManager
    }
  ].forEach(({ prop, constructorName, constructor }) => {
    instance[prop] = new instance.__managers[constructorName](instance);
    if (isDev && !(instance[prop] instanceof constructor)) {
      throw new Error(
        `The \`$managers.${constructorName}\` must extend the \`${constructorName}\` class.`
      );
    }
  });
}
class Base extends EventTarget {
  constructor(element) {
    super();
    __publicField(this, "$parent", null);
    __publicField(this, "$id");
    __publicField(this, "$el");
    __publicField(this, "$isMounted", false);
    __publicField(this, "__eventHandlers", /* @__PURE__ */ new Map());
    __publicField(this, "__services");
    __publicField(this, "__refs");
    __publicField(this, "__options");
    __publicField(this, "__children");
    __publicField(this, "__events");
    if (!element) {
      if (isDev) {
        throw new Error("The root element must be defined.");
      }
      return;
    }
    const { __config } = this;
    if (__config.name === "Base") {
      if (isDev) {
        throw new Error("The `config.name` property is required.");
      }
      return;
    }
    this.$id = `${__config.name}-${id}`;
    id += 1;
    this.$el = element;
    if (!this.$el.__base__) {
      this.$el.__base__ = /* @__PURE__ */ new WeakMap();
    }
    this.$el.__base__.set(this.__ctor, this);
    createAndTestManagers(this);
    if (isDev) {
      this.__debug("constructor", this);
    }
  }
  get $root() {
    if (!this.$parent) {
      return this;
    }
    let parent = this.$parent;
    let root = this.$parent;
    while (parent) {
      if (!parent.$parent) {
        root = parent;
      }
      parent = parent.$parent;
    }
    return root;
  }
  get __config() {
    var _a, _b, _c;
    let proto = Object.getPrototypeOf(this);
    let { config } = proto.constructor;
    while (proto.constructor.config && proto.constructor.$isBase) {
      config = { ...proto.constructor.config, ...config };
      if (proto.constructor.config.options) {
        config.options = { ...proto.constructor.config.options, ...config.options };
      }
      if (proto.constructor.config.emits && config.emits) {
        config.emits = [...proto.constructor.config.emits, ...config.emits];
      }
      proto = Object.getPrototypeOf(proto);
    }
    config.options = (_a = config.options) != null ? _a : {};
    config.refs = (_b = config.refs) != null ? _b : [];
    config.components = (_c = config.components) != null ? _c : {};
    return config;
  }
  get $services() {
    return this.__services;
  }
  get $refs() {
    return this.__refs;
  }
  get $options() {
    return this.__options;
  }
  get $children() {
    return this.__children;
  }
  get $log() {
    return this.__options.log ? window.console.log.bind(window, `[${this.__config.name}]`) : noop;
  }
  get __debug() {
    return isDev && this.__options.debug ? window.console.log.bind(window, `[debug] [${this.$id}]`) : noop;
  }
  get __managers() {
    return {
      ChildrenManager,
      EventsManager,
      OptionsManager,
      RefsManager,
      ServicesManager
    };
  }
  __callMethod(method, ...args) {
    if (isDev) {
      this.__debug("callMethod", method, ...args);
    }
    this.$emit(method, ...args);
    if (!isFunction(this[method])) {
      return null;
    }
    if (isDev) {
      this.__debug(method, this, ...args);
    }
    return this[method].call(this, ...args);
  }
  __hasEvent(event) {
    const eventHandlers = this.__eventHandlers.get(event);
    return eventHandlers && eventHandlers.size > 0;
  }
  $mount() {
    if (this.$isMounted) {
      return this;
    }
    this.$emit("before-mounted");
    this.$isMounted = true;
    if (isDev) {
      this.__debug("$mount");
    }
    this.$children.registerAll();
    this.$refs.registerAll();
    this.__events.bindRootElement();
    this.$services.enableAll();
    this.$children.mountAll();
    this.__callMethod("mounted");
    return this;
  }
  $update() {
    if (isDev) {
      this.__debug("$update");
    }
    this.$refs.unregisterAll();
    this.$services.disableAll();
    this.$children.registerAll();
    this.$refs.registerAll();
    this.$services.enableAll();
    this.$children.updateAll();
    this.__callMethod("updated");
    return this;
  }
  $destroy() {
    if (!this.$isMounted) {
      return this;
    }
    this.$isMounted = false;
    if (isDev) {
      this.__debug("$destroy");
    }
    this.__events.unbindRootElement();
    this.$refs.unregisterAll();
    this.$services.disableAll();
    this.$children.destroyAll();
    this.__callMethod("destroyed");
    return this;
  }
  $terminate() {
    if (isDev) {
      this.__debug("$terminate");
    }
    this.$destroy();
    this.__callMethod("terminated");
    this.$el.__base__.set(this.__ctor, "terminated");
  }
  __addEmits(event) {
    const ctor = this.__ctor;
    if (isArray(ctor.config.emits)) {
      ctor.config.emits.push(event);
    } else {
      ctor.config.emits = [event];
    }
  }
  __removeEmits(event) {
    const ctor = this.__ctor;
    const index = ctor.config.emits.indexOf(event);
    ctor.config.emits.splice(index, 1);
  }
  get __ctor() {
    return this.constructor;
  }
  $on(event, listener, options) {
    if (isDev) {
      this.__debug("$on", event, listener, options);
    }
    let set = this.__eventHandlers.get(event);
    if (!set) {
      set = /* @__PURE__ */ new Set();
      this.__eventHandlers.set(event, set);
    }
    set.add(listener);
    const target = getEventTarget(this, event, this.__config);
    target.addEventListener(event, listener, options);
    return () => {
      this.$off(event, listener, options);
    };
  }
  $off(event, listener, options) {
    if (isDev) {
      this.__debug("$off", event, listener);
    }
    this.__eventHandlers.get(event).delete(listener);
    const target = getEventTarget(this, event, this.__config);
    target.removeEventListener(event, listener, options);
  }
  $emit(event, ...args) {
    if (isDev) {
      this.__debug("$emit", event, args);
    }
    this.dispatchEvent(new CustomEvent(event, { detail: args }));
  }
  static $factory(nameOrSelector) {
    if (isDev && !nameOrSelector) {
      throw new Error(
        "The $factory method requires a component\u2019s name or selector to be specified."
      );
    }
    return getComponentElements(nameOrSelector).map((el) => new this(el).$mount());
  }
}
__publicField(Base, "$isBase", true);
__publicField(Base, "config", {
  name: "Base",
  emits: [
    "before-mounted",
    "mounted",
    "updated",
    "destroyed",
    "terminated",
    "ticked",
    "scrolled",
    "resized",
    "moved",
    "loaded",
    "keyed"
  ]
});
export {
  Base
};


if (module.hot) {module.hot.accept(function(err) {
if (err) {
console.error(err);
}
});
}
